import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchUserProfileClaims } from 'common/services/api/UserProfileClaims/UserProfileClaims.thunk';
import { Claim } from 'common/types/Entities';
import { createMemoizedKeySelector } from 'common/utils/selectorUtils';

interface UserProfileClaimsSliceState {
    userClaimsByTenantId: Record<string, Claim[]>;
    processing: string | false;
}

type StateWithUserProfileClaims = { userProfileClaims: UserProfileClaimsSliceState };

const initialState: UserProfileClaimsSliceState = {
    userClaimsByTenantId: {},
    processing: false,
};

const userProfileClaimsSlice = createSlice({
    name: 'userProfileClaims',
    initialState,

    reducers: (create) => ({
        clearUserProfileClaims: create.reducer(state => {
            state.userClaimsByTenantId = {};
        })
    }),

    extraReducers: builder => {
        builder.addCase(fetchUserProfileClaims.pending, (state, args) => {
            state.processing = args.meta.arg.tenantId;
            state.userClaimsByTenantId[args.meta.arg.tenantId] = [];
        });
        builder.addCase(fetchUserProfileClaims.fulfilled, (state, action: PayloadAction<Claim[]>) => {
            state.processing = false;
            if(action.payload.length) {
                const tenantId = action.payload[0].tenantId;
                state.userClaimsByTenantId = {
                    ...state.userClaimsByTenantId,
                    [tenantId]: action.payload
                };
            }
        });
        builder.addCase(fetchUserProfileClaims.rejected, (state, args) => {
            state.processing = false;
            state.userClaimsByTenantId[args.meta.arg.tenantId] = [];
        });
    }
});

// eslint-disable-next-line no-restricted-syntax
export default userProfileClaimsSlice.reducer;
export const { clearUserProfileClaims } = userProfileClaimsSlice.actions;

// Base selector for byTenantId state
const selectUserClaimsByTenantIdState = (state: StateWithUserProfileClaims) => state.userProfileClaims.userClaimsByTenantId;

// Memoized parameterized selector
export const selectUserProfileClaimsByTenantId = createMemoizedKeySelector(selectUserClaimsByTenantIdState);
export const selectProcessing = (state: StateWithUserProfileClaims) => state.userProfileClaims.processing;
